/**
 * CreateTripModal Component - Form for creating a new trip
 *
 * Operator rule: Partner field is always required (Bubakii fleet or a third-party partner).
 * Field order: Trip intent → Corridor → Partner → Capacity → Vehicle / Driver → rest.
 * Capacity-first: capacityClass always required; vehicle optional for open shared load,
 * required for schedule when partner has fleet. Both may be set (not XOR).
 */

import React, { useState, useEffect, useCallback } from "react";
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Spin,
  Tag,
  Radio,
  Tooltip,
} from "antd";
import { PlusOutlined, BankOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { getVehicles } from "../../vehicles/services/vehicleService";
import { getDrivers } from "../../drivers/services/driverService";
import { getOrders } from "../../orders/services/orderService";
import { getActiveRouteCorridors } from "../../routeCorridors/services/routeCorridorService";
import { getPartners } from "../../partners/services/partnerService";
import { getSetting } from "../../systemSettings/services/systemSettingsService.js";
import { createTrip } from "../services/tripService";
import { logError, logInfo } from "../../../shared/utils/logger.js";
import { filterSelectOption } from "../../../shared/utils/selectFilterOption.js";
import { resolveRegistryCorridor } from "../../../shared/utils/routeCorridorUtils.js";
import {
  BUBAKII_PROVIDER,
  isBubakiiProvider,
} from "../../drivers/utils/providerDriverAssignment";
import {
  ACCEPTING_LOAD_TRIP_STATUS,
  PLANNED_TRIP_STATUS,
} from "../constants/tripStatuses.js";
import DateTimeField from "../../../components/DateTimeField";
import { useDeviceType } from "../../leads/hooks/useDeviceType.js";
import dayjs from "dayjs";
import {
  deriveCapacityClassFromVehicle,
  driverFiltersForPartnerSelection,
  FALLBACK_CAPACITY_CLASS_OPTIONS,
  isVehicleRequiredForIntent,
  noFleetScheduleWarning,
  partnerHasNoVehicles,
  partnerSelectionToOperator,
  resolveCapacityClassOptions,
  shouldBlockSubmitForMissingFleet,
  shouldShowDriverField,
  shouldShowVehicleField,
  vehicleFiltersForPartnerSelection,
} from "../utils/tripOperatorFields.js";

const { TextArea } = Input;
const { Text } = Typography;
const { Option } = Select;

// Helper function to extract province from order location
const getProvince = (location) => {
  // Try addressComponents first (structured data)
  if (location?.addressComponents?.province) {
    return location.addressComponents.province;
  }

  // Fallback: try to parse from address string
  if (location?.address) {
    const addressParts = location.address.split(",").map((p) => p.trim());
    // Province is usually last or second-to-last part
    const possibleProvince =
      addressParts[addressParts.length - 1] ||
      addressParts[addressParts.length - 2];
    return possibleProvince || "N/A";
  }

  return "N/A";
};

// Format order display: Order ID - Customer Name - Pickup Province → Delivery Province
const formatOrderDisplay = (order) => {
  const customer = order.customer || "N/A";
  const pickupProvince = getProvince(order.pickup);
  const deliveryProvince = getProvince(order.delivery);
  return `${order.id} - ${customer} - ${pickupProvince} → ${deliveryProvince}`;
};

const CreateTripModal = ({ open, onSuccess, onCancel, prefill = null }) => {
  const { isMobile } = useDeviceType();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [partners, setPartners] = useState([]);
  const [orders, setOrders] = useState([]);
  const [routeCorridors, setRouteCorridors] = useState([]);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingRouteCorridors, setLoadingRouteCorridors] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [tripIntent, setTripIntent] = useState(PLANNED_TRIP_STATUS);
  const [selectedPartnerId, setSelectedPartnerId] = useState(undefined);
  const [capacityOptions, setCapacityOptions] = useState([
    ...FALLBACK_CAPACITY_CLASS_OPTIONS,
  ]);

  const loadAssetsForPartner = useCallback(async (partnerSelectionId) => {
    if (!partnerSelectionId) {
      setVehicles([]);
      setDrivers([]);
      return;
    }

    const vehicleFilters = vehicleFiltersForPartnerSelection(partnerSelectionId);
    const driverFilters = driverFiltersForPartnerSelection(partnerSelectionId);

    try {
      setLoadingVehicles(true);
      setLoadingDrivers(true);

      const [vehicleResponse, driverResponse] = await Promise.all([
        vehicleFilters
          ? getVehicles(vehicleFilters)
          : Promise.resolve({ success: true, data: [] }),
        driverFilters
          ? getDrivers(driverFilters)
          : Promise.resolve({ success: true, data: [] }),
      ]);

      setVehicles(
        vehicleResponse.success && vehicleResponse.data
          ? vehicleResponse.data || []
          : []
      );
      setDrivers(
        driverResponse.success && driverResponse.data
          ? driverResponse.data || []
          : []
      );
    } catch (err) {
      logError("Failed to fetch partner assets", { error: err.message });
      setVehicles([]);
      setDrivers([]);
    } finally {
      setLoadingVehicles(false);
      setLoadingDrivers(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      fetchPartners();
      fetchOrders();
      fetchRouteCorridors();
      fetchCapacityOptions();
      form.resetFields();
      setError(null);
      setVehicles([]);
      setDrivers([]);
      setSelectedPartnerId(undefined);

      const orderIds = (prefill?.orderIds || []).filter(Boolean);
      setSelectedOrderIds(orderIds);
      setTripIntent(PLANNED_TRIP_STATUS);
      form.setFieldsValue({
        routeCorridor: "",
        vehicleId: undefined,
        capacityClass: undefined,
        partnerId: undefined,
        driverId: undefined,
        tripIntent: PLANNED_TRIP_STATUS,
        departureDate: dayjs(),
        orderIds,
        notes: "",
      });
    }
  }, [open, prefill, form]);

  const fetchCapacityOptions = async () => {
    try {
      const response = await getSetting("pricing.vehicles.bands");
      const bands = response?.data?.value ?? response?.value ?? response?.data;
      setCapacityOptions(resolveCapacityClassOptions(bands));
    } catch (err) {
      logError("Failed to fetch capacity class options", { error: err.message });
      setCapacityOptions([...FALLBACK_CAPACITY_CLASS_OPTIONS]);
    }
  };

  useEffect(() => {
    if (!open || !prefill?.routeCorridor || routeCorridors.length === 0) {
      return;
    }

    const registryNames = routeCorridors.map((route) => route.name);
    const resolved = resolveRegistryCorridor(prefill.routeCorridor, registryNames);
    if (resolved) {
      form.setFieldValue("routeCorridor", resolved);
    }
  }, [open, prefill, routeCorridors, form]);

  useEffect(() => {
    if (!open || !prefill?.orderIds?.length || orders.length === 0) {
      return;
    }

    const availableOrderIds = prefill.orderIds.filter((id) =>
      orders.some((order) => order.id === id)
    );
    if (availableOrderIds.length > 0) {
      setSelectedOrderIds(availableOrderIds);
      form.setFieldValue("orderIds", availableOrderIds);
    }
  }, [open, prefill, orders, form]);

  const fetchPartners = async () => {
    try {
      setLoadingPartners(true);
      const response = await getPartners({ isActive: true });
      if (response.success && response.data) {
        setPartners(response.data || []);
      } else {
        setPartners([]);
      }
    } catch (err) {
      logError("Failed to fetch partners", { error: err.message });
      setPartners([]);
    } finally {
      setLoadingPartners(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoadingOrders(true);
      const response = await getOrders({ status: "pending,booked" });
      if (response.success && response.data) {
        // Filter orders that don't already have a tripId
        const availableOrders = (response.data || []).filter(
          (order) => !order.tripId
        );
        setOrders(availableOrders);
      }
    } catch (err) {
      logError("Failed to fetch orders", { error: err.message });
    } finally {
      setLoadingOrders(false);
    }
  };

  const fetchRouteCorridors = async () => {
    try {
      setLoadingRouteCorridors(true);
      const routeCorridorsList = await getActiveRouteCorridors();
      setRouteCorridors(routeCorridorsList || []);
    } catch (err) {
      logError("Failed to fetch route corridors", { error: err.message });
      // Fallback to empty array if fetch fails
      setRouteCorridors([]);
    } finally {
      setLoadingRouteCorridors(false);
    }
  };

  const handlePartnerChange = (partnerSelectionId) => {
    setSelectedPartnerId(partnerSelectionId);
    form.setFieldsValue({ vehicleId: undefined, driverId: undefined });
    if (!partnerSelectionId) {
      setVehicles([]);
      setDrivers([]);
      return;
    }
    loadAssetsForPartner(partnerSelectionId);
  };

  const handleVehicleChange = (vehicleId) => {
    if (!vehicleId) return;
    const vehicle = vehicles.find((v) => v.id === vehicleId);
    const derived = deriveCapacityClassFromVehicle(vehicle, capacityOptions);
    if (derived) {
      form.setFieldValue("capacityClass", derived);
    }
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      setError(null);

      // Get current form values directly
      const formValues = form.getFieldsValue();

      // Use formValues.orderIds as fallback if values.orderIds is empty
      // Also check selectedOrderIds state as final fallback
      const orderIds =
        values.orderIds?.length > 0
          ? values.orderIds
          : formValues.orderIds?.length > 0
            ? formValues.orderIds
            : selectedOrderIds?.length > 0
              ? selectedOrderIds
              : [];

      const intent = values.tripIntent || tripIntent;
      const isAcceptingLoad = intent === ACCEPTING_LOAD_TRIP_STATUS;
      const { ownerType, partnerId } = partnerSelectionToOperator(
        values.partnerId
      );

      const tripData = {
        routeCorridor: values.routeCorridor,
        capacityClass: values.capacityClass,
        ownerType,
        partnerId,
        status: isAcceptingLoad
          ? ACCEPTING_LOAD_TRIP_STATUS
          : PLANNED_TRIP_STATUS,
        notes: values.notes || "",
        orderIds: orderIds,
      };

      if (values.vehicleId) {
        tripData.vehicleId = values.vehicleId;
      }

      if (values.driverId) {
        tripData.driverId = values.driverId;
      }

      if (values.departureDate) {
        tripData.departureDate = values.departureDate.toISOString();
      }

      const result = await createTrip(tripData);
      if (result.success) {
        logInfo("Trip created successfully", { tripId: result.data?.id });
        form.resetFields();
        setSelectedOrderIds([]);
        setSelectedPartnerId(undefined);
        setVehicles([]);
        setDrivers([]);
        if (onSuccess) {
          onSuccess(result.data);
        }
      } else {
        setError(result.error || "Failed to create trip");
      }
    } catch (err) {
      logError("Failed to create trip", { error: err.message });
      setError(err.message || "Failed to create trip");
    } finally {
      setLoading(false);
    }
  };

  const partnerOptions = [BUBAKII_PROVIDER, ...partners];
  const showVehicle = shouldShowVehicleField(
    selectedPartnerId,
    vehicles,
    loadingVehicles
  );
  const showDriver = shouldShowDriverField(
    selectedPartnerId,
    drivers,
    loadingDrivers
  );
  const noVehiclesForPartner = partnerHasNoVehicles(
    selectedPartnerId,
    vehicles,
    loadingVehicles
  );
  const vehicleRequired = isVehicleRequiredForIntent(
    tripIntent,
    showVehicle
  );
  const fleetWarning = noFleetScheduleWarning(
    tripIntent,
    noVehiclesForPartner
  );
  const assetsLoading =
    Boolean(selectedPartnerId) && (loadingVehicles || loadingDrivers);
  const blockForMissingFleet = shouldBlockSubmitForMissingFleet();

  return (
    <Modal
      title={
        <Space>
          <PlusOutlined style={{ color: "#1890ff" }} />
          <Text strong>Create New Trip</Text>
        </Space>
      }
      open={open}
      onCancel={onCancel}
      footer={null}
      width={isMobile ? "calc(100vw - 24px)" : 700}
      centered
      destroyOnHidden
      styles={
        isMobile
          ? {
              body: {
                maxHeight: "calc(100vh - 120px)",
                overflowY: "auto",
                overflowX: "hidden",
                WebkitOverflowScrolling: "touch",
                // Extra space so native date/time pickers near the bottom
                // are less likely to clip against the modal edge.
                paddingBottom: 48,
              },
            }
          : undefined
      }
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          routeCorridor: "",
          vehicleId: undefined,
          capacityClass: undefined,
          partnerId: undefined,
          driverId: undefined,
          tripIntent: PLANNED_TRIP_STATUS,
          departureDate: dayjs(),
          orderIds: [],
          notes: "",
        }}
      >
        <Form.Item
          name="tripIntent"
          label={
            <Space size={4}>
              Trip intent
              <Tooltip
                placement="top"
                title={
                  <div>
                    <div>
                      <strong>Open shared load:</strong> Publish or accept
                      capacity for shared-load matching (marketplace / PWA).
                      Departure may be set later when you schedule departure.
                    </div>
                    <div style={{ marginTop: 8 }}>
                      <strong>Schedule trip:</strong> Plan a concrete departure
                      (date/time required) for an assigned run on a corridor.
                    </div>
                  </div>
                }
              >
                <span
                  tabIndex={0}
                  role="button"
                  aria-label="Explain Open shared load vs Schedule trip"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    color: "rgba(0, 0, 0, 0.45)",
                    cursor: "help",
                    fontSize: 14,
                    outline: "none",
                  }}
                >
                  <InfoCircleOutlined />
                </span>
              </Tooltip>
            </Space>
          }
          rules={[{ required: true, message: "Select trip intent" }]}
        >
          <Radio.Group
            onChange={(event) => {
              const nextIntent = event.target.value;
              setTripIntent(nextIntent);
              if (nextIntent === ACCEPTING_LOAD_TRIP_STATUS) {
                form.setFieldValue("departureDate", null);
              } else if (!form.getFieldValue("departureDate")) {
                form.setFieldValue("departureDate", dayjs());
              }
            }}
          >
            <Radio.Button value={ACCEPTING_LOAD_TRIP_STATUS}>
              Open shared load
            </Radio.Button>
            <Radio.Button value={PLANNED_TRIP_STATUS}>
              Schedule trip
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        <Form.Item
          name="routeCorridor"
          label="Route Corridor"
          rules={[
            {
              required: true,
              message: "Please select a route corridor",
            },
          ]}
        >
          <Select
            placeholder="Select route corridor"
            showSearch
            loading={loadingRouteCorridors}
            optionFilterProp="label"
            filterOption={filterSelectOption}
            notFoundContent={
              loadingRouteCorridors ? (
                <Spin size="small" />
              ) : routeCorridors.length === 0 ? (
                "No route corridors available"
              ) : null
            }
          >
            {routeCorridors.map((route) => (
              <Option
                key={route.id}
                value={route.name}
                label={
                  route.description
                    ? `${route.name} - ${route.description}`
                    : route.name
                }
              >
                {route.name}
                {route.description && ` - ${route.description}`}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="partnerId"
          label="Partner"
          rules={[
            {
              required: true,
              message: "Please select a partner",
            },
          ]}
          help="Who operates this trip — Bubakii fleet or a third-party partner"
        >
          <Select
            placeholder="Select partner"
            showSearch
            allowClear={false}
            loading={loadingPartners}
            optionFilterProp="label"
            filterOption={filterSelectOption}
            onChange={handlePartnerChange}
            optionLabelProp="label"
            data-testid="create-trip-partner-select"
          >
            {partnerOptions.map((partner) => (
              <Option
                key={partner.id}
                value={partner.id}
                label={partner.name}
              >
                <Space>
                  <BankOutlined />
                  <Text strong>{partner.name}</Text>
                  <Tag color={isBubakiiProvider(partner) ? "blue" : "orange"}>
                    {isBubakiiProvider(partner) ? "Bubakii" : "3rd Party"}
                  </Tag>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        {assetsLoading && (
          <Form.Item>
            <Space>
              <Spin size="small" />
              <Text type="secondary">Loading vehicles and drivers…</Text>
            </Space>
          </Form.Item>
        )}

        {fleetWarning && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={
              tripIntent === ACCEPTING_LOAD_TRIP_STATUS
                ? "No vehicles for this partner"
                : "No truck assigned"
            }
            description={fleetWarning}
            data-testid="create-trip-no-vehicles"
          />
        )}

        <Form.Item
          name="capacityClass"
          label="Capacity"
          rules={[
            {
              required: true,
              message: "Please select a capacity class",
            },
          ]}
          help={
            tripIntent === ACCEPTING_LOAD_TRIP_STATUS
              ? "Required for shared-load matching (marketplace / PWA)"
              : "Auto-fills from vehicle when selected; you can still change it"
          }
        >
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            data-testid="create-trip-capacity-class"
          >
            {capacityOptions.map((option) => (
              <Radio.Button key={option.value} value={option.value}>
                {option.label}
              </Radio.Button>
            ))}
          </Radio.Group>
        </Form.Item>

        {showVehicle && (
          <Form.Item
            name="vehicleId"
            label={
              vehicleRequired ? "Vehicle" : "Vehicle (Optional)"
            }
            rules={[
              {
                required: vehicleRequired,
                message: "Please select a vehicle",
              },
            ]}
            help={
              vehicleRequired
                ? "Vehicles belonging to the selected partner"
                : "Optional for open shared load — assign a plate when known"
            }
          >
            <Select
              placeholder={
                vehicleRequired
                  ? "Select vehicle"
                  : "Select vehicle (optional)"
              }
              showSearch
              allowClear={!vehicleRequired}
              loading={loadingVehicles}
              optionFilterProp="label"
              filterOption={filterSelectOption}
              onChange={handleVehicleChange}
              data-testid="create-trip-vehicle-select"
            >
              {vehicles.map((vehicle) => (
                <Option
                  key={vehicle.id}
                  value={vehicle.id}
                  label={`${vehicle.licensePlate} - ${vehicle.make} ${vehicle.model} (${vehicle.vehicleType})`}
                >
                  {vehicle.licensePlate} - {vehicle.make} {vehicle.model} (
                  {vehicle.vehicleType})
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        {showDriver && (
          <Form.Item
            name="driverId"
            label="Driver (Optional)"
            help="Optional — assign a driver now or later"
          >
            <Select
              placeholder="Select driver (optional)"
              showSearch
              allowClear
              loading={loadingDrivers}
              optionFilterProp="label"
              filterOption={filterSelectOption}
              data-testid="create-trip-driver-select"
            >
              {drivers.map((driver) => (
                <Option
                  key={driver.id}
                  value={driver.id}
                  label={`${driver.name} (${driver.phone})`}
                >
                  {driver.name} ({driver.phone})
                </Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item
          name="departureDate"
          label="Departure Date & Time"
          rules={[
            {
              required: tripIntent !== ACCEPTING_LOAD_TRIP_STATUS,
              message: "Please select departure date and time",
            },
          ]}
          help={
            tripIntent === ACCEPTING_LOAD_TRIP_STATUS
              ? "Optional while accepting load — set when you schedule departure"
              : undefined
          }
        >
          <DateTimeField disablePast aria-label="Departure date and time" />
        </Form.Item>

        <Form.Item
          name="orderIds"
          label="Orders (Optional)"
          getValueFromEvent={(value) => {
            // Ensure we always return an array
            if (!value) return [];
            const result = Array.isArray(value) ? value : [value];
            setSelectedOrderIds(result); // Track for helper text
            return result;
          }}
          help={
            selectedOrderIds.length > 0
              ? `${selectedOrderIds.length} order${selectedOrderIds.length > 1 ? "s" : ""} selected`
              : "Pending and booked orders without trips are shown"
          }
        >
          <Select
            mode="multiple"
            placeholder="Select orders to add to trip"
            loading={loadingOrders}
            optionFilterProp="label"
            allowClear
            showSearch
            filterOption={filterSelectOption}
          >
            {orders.map((order) => (
              <Option
                key={order.id}
                value={order.id}
                label={formatOrderDisplay(order)}
              >
                {formatOrderDisplay(order)}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item name="notes" label="Notes (Optional)">
          <TextArea rows={3} placeholder="Add any notes about this trip..." />
        </Form.Item>

        <Form.Item>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={onCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={blockForMissingFleet || assetsLoading}
              data-testid="create-trip-submit"
            >
              Create Trip
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreateTripModal;
